import { Segmented } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { WorkflowTemplate } from '@/api/workflows';
import { BmoModal } from '@/components/BmoModal';
import { FormShell } from '@/form-renderer/FormShell';
import {
  DevicePreviewFrame,
  DeviceViewportSwitcher,
  type DeviceKind,
} from '@/originator/DevicePreviewFrame';

type PreviewSide = 'previous' | 'pending';

type Props = {
  open: boolean;
  onCancel: () => void;
  /** Frozen pending-version template — the caller is expected to pass a
   *  template whose `.steps` is the version-under-review's snapshot, NOT the
   *  template's live state. `WorkflowReview` builds this by cloning
   *  `template` and swapping `.steps` with `latestVersion.steps`. */
  template: WorkflowTemplate;
  /** Optional frozen predecessor template — when present, the modal shows a
   *  "previous ↔ pending" version toggle so the approver can flip between
   *  what advisors see today (v1) and what they'll see if this pending
   *  version is approved. Absent on first submissions (no v1). */
  prevTemplate?: WorkflowTemplate;
  /** Label rendered on the version toggle for the pending side (e.g. "v1.1"). */
  nextLabel: string;
  /** Label rendered on the version toggle for the previous side (e.g. "v1").
   *  Ignored when `prevTemplate` is absent. */
  prevLabel?: string;
  /** Optional step id to land on when the preview opens — lets the caller
   *  deep-link to a specific diff row's target step. */
  initialStepId?: string;
};

/**
 * AC2 on "Preview and reject all workflow changes at once": lets the approver
 * preview the workflow (in all viewport modes) from within the review page
 * without navigating away. Wraps the same `FormShell` + `DevicePreviewFrame`
 * the standalone `/preview` page uses, inside a full-viewport `<BmoModal>`
 * so the diff panel underneath keeps its full width when the preview is
 * closed.
 *
 * `mode="preview"` on FormShell — advisor input state is throwaway, no data
 * saves, no advisor-side effects fire. Same shell the eventual advisor route
 * will use so what an approver sees in preview matches what the advisor
 * will experience once that route ships.
 */
export function InlineWorkflowPreviewModal({
  open,
  onCancel,
  template,
  prevTemplate,
  nextLabel,
  prevLabel,
  initialStepId,
}: Props) {
  const { t, i18n } = useTranslation();
  const language = i18n.language.startsWith('fr') ? 'fr' : 'en';
  // Local viewport state — mirrors PreviewPage. Resets on modal close so a
  // subsequent open lands back on Desktop by default.
  const [device, setDevice] = useState<DeviceKind>('desktop');
  // Which frozen snapshot to render — pending is the default (that's what
  // the approver is reviewing). Only the pending option shows when the
  // workflow has no predecessor (first submission).
  const [side, setSide] = useState<PreviewSide>('pending');
  const hasPrev = Boolean(prevTemplate);
  const activeTemplate = side === 'previous' && prevTemplate ? prevTemplate : template;

  return (
    <BmoModal
      open={open}
      onCancel={() => {
        setDevice('desktop');
        setSide('pending');
        onCancel();
      }}
      // Fullscreen: 100vw wide, pinned to `top: 0` so it sits flush with the
      // viewport, and content flex-columned to 100vh so the body region
      // owns the vertical space. The body is `overflow: auto` so a long form
      // scrolls INSIDE the modal instead of pushing the chrome off-screen —
      // Preview must always give the approver the header + close affordance,
      // no matter how long the workflow.
      width="100vw"
      style={{ top: 0, paddingBottom: 0, maxWidth: '100vw', margin: 0 }}
      contentStyle={{ height: '100vh', display: 'flex', flexDirection: 'column' }}
      title={t('approver.review.previewModal.title', { defaultValue: 'Preview' })}
      bodyStyle={{
        padding: '16px 20px 20px',
        background: '#f6f7f8',
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 12,
          flexShrink: 0,
        }}
      >
        {hasPrev ? (
          <Segmented<PreviewSide>
            value={side}
            onChange={setSide}
            aria-label={t('approver.review.previewModal.versionSwitcher', {
              defaultValue: 'Version',
            })}
            options={[
              {
                value: 'previous',
                label: t('approver.review.previewModal.previousOption', {
                  defaultValue: '{{label}} (live)',
                  label: prevLabel ?? '',
                }),
              },
              {
                value: 'pending',
                label: t('approver.review.previewModal.pendingOption', {
                  defaultValue: '{{label}} (pending)',
                  label: nextLabel,
                }),
              },
            ]}
          />
        ) : (
          // Preserve the toggle's slot so Desktop / Tablet / Mobile stays
          // right-aligned regardless of whether the version toggle is shown.
          <div />
        )}
        <DeviceViewportSwitcher device={device} onChange={setDevice} />
      </div>
      <DevicePreviewFrame device={device}>
        <FormShell
          // key={side} — force FormShell to remount when the approver flips
          // versions. Without it, FormShell's per-step state (activeStepId,
          // input values) leaks from the pending render into the previous
          // one and vice versa, which reads as "the toggle didn't flip".
          key={side}
          template={activeTemplate}
          language={language}
          mode="preview"
          initialStepId={initialStepId}
        />
      </DevicePreviewFrame>
    </BmoModal>
  );
}
